import React from 'react';
import {
  CatalogIndexPage,
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import { DefaultFilters } from '@backstage/plugin-catalog-react';
import { Chip } from '@material-ui/core';
import CloudQueueIcon from '@material-ui/icons/CloudQueue';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import BuildIcon from '@material-ui/icons/Build';
import CameraIcon from '@material-ui/icons/Camera';

/**
 * 获取 System 的管理方式
 * @param entity System 实体
 * @returns 管理方式: 'knative' | 'argocd' | 'ssa' | 'kubernetes'
 * 
 * 判断逻辑：
 * 统一使用 hose.io/powered-by 注解
 * 1. hose.io/powered-by: kubernetes → Kubernetes
 * 2. hose.io/powered-by: knative → Knative
 * 3. hose.io/powered-by: ssa → SSA
 * 4. 其他（默认）→ ArgoCD（兼容已上线的老数据）
 */
function getManagementType(entity: any): 'knative' | 'argocd' | 'ssa' | 'kubernetes' {
  const annotations = entity.metadata?.annotations || {};
  const poweredBy = annotations['hose.io/powered-by'];
  
  // 检查是否是 Kubernetes 原生管理
  if (poweredBy === 'kubernetes') {
    return 'kubernetes';
  }
  
  // 检查是否是 Knative 管理
  if (poweredBy === 'knative') {
    return 'knative';
  }
  
  // 检查是否是 SSA 管理
  if (poweredBy === 'ssa') {
    return 'ssa';
  }
  
  // 默认是 ArgoCD（兼容已上线的老数据）
  return 'argocd';
}

/**
 * 管理方式显示组件
 */
const ManagementTypeChip: React.FC<{ type: 'knative' | 'argocd' | 'ssa' | 'kubernetes' }> = ({ type }) => {
  const configs = {
    kubernetes: {
      label: 'Kubernetes',
      icon: <CameraIcon style={{ fontSize: 16 }} />,
      bgColor: 'rgba(50, 108, 229, 0.12)',  // K8s 蓝色 12% 透明度
      textColor: '#326ce5',  // Kubernetes 品牌蓝色
    },
    knative: {
      label: 'Knative',
      icon: <CloudQueueIcon style={{ fontSize: 16 }} />,
      bgColor: 'rgba(25, 118, 210, 0.12)',  // 蓝色 12% 透明度
      textColor: '#1565c0',  // 深蓝色文字
    },
    argocd: {
      label: 'ArgoCD',
      icon: <MergeTypeIcon style={{ fontSize: 16 }} />,
      bgColor: 'rgba(124, 77, 255, 0.12)',  // 紫色 12% 透明度
      textColor: '#5e35b1',  // 深紫色文字
    },
    ssa: {
      label: 'SSA',
      icon: <BuildIcon style={{ fontSize: 16 }} />,
      bgColor: 'rgba(120, 144, 156, 0.12)',  // 蓝灰色 12% 透明度
      textColor: '#546e7a',  // 深蓝灰色文字
    },
  };
  
  const config = configs[type];
  
  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      style={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 500,
        paddingLeft: 6,
        paddingRight: 10,
      }}
    />
  );
};

/**
 * 创建 System 列表页面的自定义列
 * 显示：名称、管理方式、说明、操作
 */
const createSystemsColumns: CatalogTableColumnsFunc = (entityListContext) => {
  // 如果是 system 类型，创建自定义列
  if (entityListContext.filters.kind?.value === 'system') {
    // 获取默认列以获取 Actions 列
    const defaultColumns = CatalogTable.defaultColumnsFunc(entityListContext);
    const actionsColumn = defaultColumns.find(
      col => col.id === 'actions' || col.title === 'Actions'
    );
    
    // 使用默认的名称列
    const nameColumn = CatalogTable.columns.createNameColumn({ defaultKind: 'system' });
    
    // 创建管理方式列
    const managementColumn = {
      id: 'management',
      title: 'Managed By',
      field: 'entity.metadata.annotations',
      render: (row: any) => {
        const type = getManagementType(row.entity);
        return <ManagementTypeChip type={type} />;
      },
    };
    
    // 使用默认的 Owner 列
    const ownerColumn = CatalogTable.columns.createOwnerColumn();
    
    // 使用默认的说明列，宽度比 Owner 列更宽
    const descriptionColumn = {
      ...CatalogTable.columns.createMetadataDescriptionColumn(),
      cellStyle: {
        minWidth: '300px',
      },
    };
    
    // 返回：名称、管理方式、Owner、说明、操作
    const columns = [nameColumn, managementColumn, ownerColumn, descriptionColumn];
    if (actionsColumn) {
      columns.push(actionsColumn);
    }
    
    return columns;
  }
  
  // 其他类型使用默认列
  return CatalogTable.defaultColumnsFunc(entityListContext);
};

/**
 * System 列表页面
 */
export const SystemsPage: React.FC = () => {
  return (
    <CatalogIndexPage
      key="systems"
      initiallySelectedFilter="all"
      columns={createSystemsColumns}
      filters={
        <>
          <DefaultFilters initialKind="system" />
        </>
      }
    />
  );
};

